import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import BackgroundIMG from "../../../assets/leadership-bg.png";
import BannerBG from "../../../assets/get-demo-box-bg.jpg";

function LeadershipSection({ acfData }) {
  const navigate = useNavigate();

  const [modalData, setModalData] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  const openModal = (leadership) => setModalData(leadership);
  const closeModal = () => setModalData(null);

  useEffect(() => {
    if (modalData) {
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
    }
  }, [modalData]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => closeModal(), 300);
  };

  // Transform acfData.team into UI-compatible structure
  const leadershipTeam = useMemo(() => {
    if (!acfData?.team || !Array.isArray(acfData.team)) return [];

    return acfData.team.map((member) => ({
      teamIMG: member.team_member_image,
      name: member.team_member_name,
      designation: member.team_member_designation,
      content: `<p class="mb-3">${member.team_member_detail.replace(
        /\r\n/g,
        "</p><p class='mb-3'>"
      )}</p>`,
      socialLinks: {
        phone: member.team_member_phone_number,
        email: member.team_member_email,
        linkedin: member.team_member_linkedin,
      },
    }));
  }, [acfData]);

  return (
    <>
      <section
        className="py-10 md:py-16"
        style={{
          backgroundImage: `url(${BackgroundIMG})`,
        }}
      >
        <div className="w-full max-w-[1570px] px-4 mx-auto">
          <div className="flex flex-wrap gap-y-11 -mx-4">
            <div className="w-full text-center px-4">
              <h2 className="text-[30px] md:text-[40px] lg:text-[52px] font-normal leading-tight text-[#5fa3b2]">
                Laguna Creek Leadership
              </h2>
            </div>
            <div className="w-full px-4">
              <div className="flex flex-wrap -mx-4">
                {leadershipTeam.map((leadership, index) => (
                  <div
                    key={index}
                    onClick={() => openModal(leadership)}
                    className={`w-full sm:w-1/2 md:w-1/3 lg:w-1/4 xl:w-1/5 px-4 cursor-pointer ${
                      index === leadershipTeam.length - 1 ? "hidden" : ""
                    }`}
                  >
                    <div
                      className="flex flex-col justify-between px-7 py-10 hover:bg-[#5FA3B2] rounded-[21px] h-[calc(100%-16px)] mb-4 text-center transition-all duration-500 group"
                      onClick={() => navigate(`/book/${leadership.name}`)}
                    >
                      <img
                        src={leadership.teamIMG}
                        alt={leadership.name}
                        className="mx-auto mb-9 rounded-full w-[160px] h-[160px] object-cover"
                        height="160"
                        width="160"
                      />
                      <div className="mb-auto">
                        <h2 className="text-2xl font-normal leading-tight text-[#192020] mb-[5px] group-hover:text-white">
                          {leadership.name}
                        </h2>
                        <p className="text-lg font-light leading-7 text-[#333333] mb-4 group-hover:text-white">
                          {leadership.designation}
                        </p>
                      </div>
                      <ul className="flex justify-center gap-x-3">
                        {["phone", "email", "linkedin"].map((type, index) => (
                          <li key={index}>
                            <Link
                              to={
                                type === "phone"
                                  ? `tel:${leadership.socialLinks[type]}`
                                  : type === "email"
                                  ? `mailto:${leadership.socialLinks[type]}`
                                  : leadership.socialLinks[type]
                              }
                              target={
                                type === "linkedin" ? "_blank" : undefined
                              }
                              className="block w-[31px] h-[31px] bg-[#5fa3b2] flex justify-center items-center rounded-full"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {/* Conditional rendering of SVG icons */}
                              {type === "phone" ? (
                                <svg
                                  width="17"
                                  height="17"
                                  viewBox="0 0 17 17"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M12.8263 8.2348L16.4995 11.908L14.6403 13.7672C14.3163 14.0899 13.9314 14.345 13.508 14.5177C13.0845 14.6904 12.631 14.7773 12.1737 14.7733C7.92416 14.7733 2.49963 9.34878 2.49963 5.09922C2.49591 4.64189 2.58291 4.18836 2.75561 3.76487C2.9283 3.34139 3.18326 2.95635 3.50573 2.63203L5.36492 0.773438L9.03814 4.44666L6.61962 6.86518C7.33862 8.57385 8.69607 9.93445 10.4031 10.6574L12.8263 8.2348Z"
                                    fill="white"
                                  />
                                </svg>
                              ) : type === "email" ? (
                                <svg
                                  width="18"
                                  height="17"
                                  viewBox="0 0 18 17"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M8.78232 9.05289C8.34888 9.05289 7.93309 8.8809 7.62599 8.57457L2.36256 3.30271C2.48373 2.99926 2.69281 2.73908 2.96288 2.55565C3.23296 2.37221 3.55167 2.27393 3.87799 2.27344H13.6866C14.013 2.27393 14.3317 2.37221 14.6017 2.55565C14.8718 2.73908 15.0809 2.99926 15.2021 3.30271L9.93865 8.57457C9.63154 8.8809 9.21576 9.05289 8.78232 9.05289ZM8.78146 10.1427C9.50396 10.1427 10.197 9.85609 10.7085 9.34574L15.3212 4.7342V14.2734H2.2417V4.7342L6.85441 9.34574C7.36594 9.85609 8.05895 10.1427 8.78146 10.1427Z"
                                    fill="white"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  width="18"
                                  height="17"
                                  viewBox="0 0 18 17"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M15.1262 1.51465H2.91458C2.35503 1.51465 1.90039 1.97564 1.90039 2.54155V14.7309C1.90039 15.2968 2.35503 15.7578 2.91458 15.7578H15.1262C15.6857 15.7578 16.1435 15.2968 16.1435 14.7309V2.54155C16.1435 1.97564 15.6857 1.51465 15.1262 1.51465ZM6.20513 13.7231H4.09409V6.92577H6.20831V13.7231H6.20513ZM5.14961 5.99743C4.47242 5.99743 3.92559 5.44741 3.92559 4.7734C3.92559 4.0994 4.47242 3.54938 5.14961 3.54938C5.82361 3.54938 6.37363 4.0994 6.37363 4.7734C6.37363 5.45059 5.82679 5.99743 5.14961 5.99743ZM14.1183 13.7231H12.0073V10.4166C12.0073 9.62816 11.9914 8.61397 10.9105 8.61397C9.81042 8.61397 9.64192 9.47237 9.64192 10.3594V13.7231H7.53089V6.92577H9.55608V7.85412H9.5847C9.86765 7.32 10.5576 6.75727 11.5845 6.75727C13.7209 6.75727 14.1183 8.16569 14.1183 9.99695V13.7231Z"
                                    fill="white"
                                  />
                                </svg>
                              )}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {modalData && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center overflow-auto transition-opacity duration-300"
          style={{ opacity: isVisible ? 1 : 0 }}
        >
          <div
            className={`bg-white rounded-2xl p-6 md:p-10 max-w-[750px] w-full relative my-10 mx-4 transform transition-all duration-300 ease-in-out ${
              isVisible
                ? "opacity-100 translate-y-0 scale-100"
                : "opacity-0 translate-y-4 scale-95"
            }`}
            style={{
              backgroundImage: `url(${BannerBG})`,
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <button
              onClick={handleClose}
              className="absolute top-3 right-3 text-xl font-bold text-gray-600 hover:text-black"
            >
              &times;
            </button>
            <div className="md:flex gap-x-6">
              <div className="text-center min-w-[30%]">
                <img
                  src={modalData.teamIMG}
                  alt={modalData.name}
                  className="mx-auto mb-4"
                  width="120"
                  height="120"
                />
                <h3 className="text-xl font-semibold mb-2">{modalData.name}</h3>
                <p className="text-gray-600 mb-4">{modalData.designation}</p>
                <ul className="flex justify-center gap-x-3 mb-6">
                  {["phone", "email", "linkedin"].map((type, index) => (
                    <li key={index}>
                      <Link
                        to={
                          type === "phone"
                            ? `tel:${modalData.socialLinks[type]}`
                            : type === "email"
                            ? `mailto:${modalData.socialLinks[type]}`
                            : modalData.socialLinks[type]
                        }
                        target={type === "linkedin" ? "_blank" : undefined}
                        className="block w-[31px] h-[31px] bg-[#5fa3b2] flex justify-center items-center rounded-full"
                      >
                        {type === "phone" ? (
                          <svg
                            width="17"
                            height="17"
                            viewBox="0 0 17 17"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M12.8263 8.2348L16.4995 11.908L14.6403 13.7672C14.3163 14.0899 13.9314 14.345 13.508 14.5177C13.0845 14.6904 12.631 14.7773 12.1737 14.7733C7.92416 14.7733 2.49963 9.34878 2.49963 5.09922C2.49591 4.64189 2.58291 4.18836 2.75561 3.76487C2.9283 3.34139 3.18326 2.95635 3.50573 2.63203L5.36492 0.773438L9.03814 4.44666L6.61962 6.86518C7.33862 8.57385 8.69607 9.93445 10.4031 10.6574L12.8263 8.2348Z"
                              fill="white"
                            />
                          </svg>
                        ) : type === "email" ? (
                          <svg
                            width="18"
                            height="17"
                            viewBox="0 0 18 17"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M8.78232 9.05289C8.34888 9.05289 7.93309 8.8809 7.62599 8.57457L2.36256 3.30271C2.48373 2.99926 2.69281 2.73908 2.96288 2.55565C3.23296 2.37221 3.55167 2.27393 3.87799 2.27344H13.6866C14.013 2.27393 14.3317 2.37221 14.6017 2.55565C14.8718 2.73908 15.0809 2.99926 15.2021 3.30271L9.93865 8.57457C9.63154 8.8809 9.21576 9.05289 8.78232 9.05289ZM8.78146 10.1427C9.50396 10.1427 10.197 9.85609 10.7085 9.34574L15.3212 4.7342V14.2734H2.2417V4.7342L6.85441 9.34574C7.36594 9.85609 8.05895 10.1427 8.78146 10.1427Z"
                              fill="white"
                            />
                          </svg>
                        ) : (
                          <svg
                            width="18"
                            height="17"
                            viewBox="0 0 18 17"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M15.1262 1.51465H2.91458C2.35503 1.51465 1.90039 1.97564 1.90039 2.54155V14.7309C1.90039 15.2968 2.35503 15.7578 2.91458 15.7578H15.1262C15.6857 15.7578 16.1435 15.2968 16.1435 14.7309V2.54155C16.1435 1.97564 15.6857 1.51465 15.1262 1.51465ZM6.20513 13.7231H4.09409V6.92577H6.20831V13.7231H6.20513ZM5.14961 5.99743C4.47242 5.99743 3.92559 5.44741 3.92559 4.7734C3.92559 4.0994 4.47242 3.54938 5.14961 3.54938C5.82361 3.54938 6.37363 4.0994 6.37363 4.7734C6.37363 5.45059 5.82679 5.99743 5.14961 5.99743ZM14.1183 13.7231H12.0073V10.4166C12.0073 9.62816 11.9914 8.61397 10.9105 8.61397C9.81042 8.61397 9.64192 9.47237 9.64192 10.3594V13.7231H7.53089V6.92577H9.55608V7.85412H9.5847C9.86765 7.32 10.5576 6.75727 11.5845 6.75727C13.7209 6.75727 14.1183 8.16569 14.1183 9.99695V13.7231Z"
                              fill="white"
                            />
                          </svg>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-center md:text-left min-w-[50%]">
                <div
                  className="text-base font-light leading-7 text-[#333333]"
                  dangerouslySetInnerHTML={{ __html: modalData.content }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default LeadershipSection;
